import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, MapPin, Users, Check, Phone, Mail } from 'lucide-react';
import { orientationClass, businessInfo } from '../data/mock';

const OrientationPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#6E9EB9]/10 via-white to-[#F7F8FA] overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#6E9EB9]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#AF9EB8]/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#6E9EB9]/10 rounded-full mb-6">
              <Calendar size={16} className="text-[#6E9EB9]" />
              <span className="text-sm font-medium text-[#6E9EB9]">Free Event</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              {orientationClass.title}
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              {orientationClass.description}
            </p>
          </div>
        </div>
      </section>

      {/* Schedule Info */}
      <section className="bg-[#6E9EB9] py-12">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 text-white text-center">
            <div className="flex flex-col items-center">
              <div className="p-3 bg-white/20 rounded-full mb-4">
                <Calendar size={28} />
              </div>
              <h3 className="font-serif text-xl font-medium mb-2">When</h3>
              <p className="text-white/90">{orientationClass.schedule}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="p-3 bg-white/20 rounded-full mb-4">
                <Clock size={28} />
              </div>
              <h3 className="font-serif text-xl font-medium mb-2">Time</h3>
              <p className="text-white/90">{orientationClass.time}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="p-3 bg-white/20 rounded-full mb-4">
                <MapPin size={28} />
              </div>
              <h3 className="font-serif text-xl font-medium mb-2">Location</h3>
              <p className="text-white/90">{orientationClass.location}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
                What to Expect
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                What You'll Learn at Orientation
              </h2>
              <p className="text-[#4A5568] mb-8 leading-relaxed">
                Our free orientation class is the perfect introduction to Traditional Naturopathy 
                and our approach to wellness. It's a great opportunity to learn about what we do, 
                ask questions, and see if our services are right for you.
              </p>
              <ul className="space-y-4">
                {orientationClass.topics.map((topic, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="p-1 bg-[#6E9EB9]/10 rounded-full mt-0.5 flex-shrink-0">
                      <Check size={16} className="text-[#6E9EB9]" />
                    </div>
                    <span className="text-[#4A5568]">{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#F7F8FA] to-[#6E9EB9]/5 rounded-2xl p-8 md:p-10">
              <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-6">
                Who Should Attend?
              </h3>
              <ul className="space-y-4">
                {[
                  "Anyone curious about natural health approaches",
                  "Those considering nutritional therapy",
                  "People wanting to learn about detox support",
                  "Anyone seeking alternatives to conventional approaches",
                  "Family members and friends are welcome!"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="p-1 bg-[#AF9EB8]/10 rounded-full mt-0.5 flex-shrink-0">
                      <Users size={16} className="text-[#AF9EB8]" />
                    </div>
                    <span className="text-[#4A5568]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Reserve Your Seat */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                Reserve Your Spot
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
                How to RSVP
              </h2>
              <div className="floral-divider w-24 mx-auto mb-6" />
              <p className="text-[#4A5568]">
                Space is limited. Please contact us to reserve your seat for the next orientation class.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="grid md:grid-cols-2 gap-8">
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#6E9EB9]/5 hover:bg-[#6E9EB9]/10 transition-colors"
                >
                  <div className="p-3 bg-[#6E9EB9]/10 rounded-full">
                    <Phone size={24} className="text-[#6E9EB9]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Call Us</h4>
                    <p className="text-[#6E9EB9]">{businessInfo.phone}</p>
                  </div>
                </a>
                <a 
                  href={`mailto:${businessInfo.email}?subject=Orientation Class RSVP`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#AF9EB8]/5 hover:bg-[#AF9EB8]/10 transition-colors"
                >
                  <div className="p-3 bg-[#AF9EB8]/10 rounded-full">
                    <Mail size={24} className="text-[#AF9EB8]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Email Us</h4>
                    <p className="text-[#AF9EB8]">{businessInfo.email}</p>
                  </div>
                </a>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                <p className="text-[#4A5568] mb-6">
                  Prefer to fill out a form? Visit our contact page to send us a message.
                </p>
                <Link to="/contact" className="btn-primary">
                  Go to Contact Page
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Dates */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
              Mark Your Calendar
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Upcoming Orientation Classes
            </h2>
            <div className="floral-divider w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* These would be dynamic in a real implementation */}
            {[
              { date: "August 5, 2025", day: "1st Tuesday" },
              { date: "August 19, 2025", day: "3rd Tuesday" },
              { date: "September 2, 2025", day: "1st Tuesday" },
              { date: "September 16, 2025", day: "3rd Tuesday" }
            ].map((event, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#6E9EB9] hover:shadow-sm transition-all"
              >
                <div className="p-3 bg-[#6E9EB9]/10 rounded-full">
                  <Calendar size={24} className="text-[#6E9EB9]" />
                </div>
                <div>
                  <p className="font-medium text-[#2C2F36]">{event.date}</p>
                  <p className="text-sm text-[#718096]">{event.day} • 6:30 PM</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-[#718096] mt-8">
            Classes are held at {orientationClass.location}
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6E9EB9] to-[#93A8C7] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Can't Wait for Orientation?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            If you're ready to get started right away, you can schedule a consultation 
            directly with {businessInfo.owner.name}.
          </p>
          <Link 
            to="/contact" 
            className="px-8 py-4 bg-white text-[#6E9EB9] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
          >
            Book Consultation Now
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OrientationPage;